import React from 'react';
import { TrendingUp, Shield, Clock, Target } from 'lucide-react';
import { useContent } from '../hooks/useContent';

export const ProofSection = () => {
  const { content, loading } = useContent();
  
  if (loading || !content) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-navy-900 text-xl">Loading results...</div>
        </div>
      </section>
    );
  }

  const { proof } = content;
  
  const icons = [
    <Clock className="h-8 w-8 text-blue-600" />,
    <Shield className="h-8 w-8 text-green-600" />,
    <TrendingUp className="h-8 w-8 text-purple-600" />,
    <Target className="h-8 w-8 text-orange-600" />
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-navy-900 mb-4">
            {proof.sectionTitle.split(' ')[0]} <span className="text-blue-600">{proof.sectionTitle.split(' ')[1]}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {proof.sectionDescription}
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {proof.metrics.map((metric, index) => (
            <div 
              key={index} 
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center group hover:transform hover:scale-105"
            >
              <div className="mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                {icons[index]}
              </div>
              
              <div className="text-4xl font-bold text-navy-900 mb-2">
                {metric.value}
              </div>
              
              <div className="text-lg font-semibold text-gray-800 mb-1">
                {metric.label}
              </div>
              
              <div className="text-sm text-gray-600">
                {metric.subtitle}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Image */}
        <div className="mt-16">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1669216368818-8dcc28259724?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxwaGFybWFjZXV0aWNhbCUyMGxhYm9yYXRvcnl8ZW58MHx8fHwxNzU5OTI4MDUxfDA&ixlib=rb-4.1.0&q=85"
              alt="Laboratory technicians working with pharmaceutical equipment demonstrating precision and compliance"
              className="w-full h-64 md:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 via-navy-900/60 to-navy-900/40 flex items-center">
              <div className="p-8 md:p-12 text-white max-w-2xl">
                <h3 className="text-4xl md:text-5xl font-black mb-4 text-blue-600">
                  {proof.bottomSection.heading}
                </h3>
                <p className="text-xl md:text-2xl font-medium text-black">
                  {proof.bottomSection.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};